import {FC, useCallback, useEffect, useRef, useState} from 'react';

import {Wrapper, Status} from '@googlemaps/react-wrapper';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import {IconButton, Typography} from '@mui/material';
import {useDebounce} from 'use-debounce';

import {Loading} from 'components/loader/Loading';
import {config} from 'config/env';
import useAnalytics, {ANALYTICS, SUGGESTEDLIST_CHANNELS, EVENT_SOURCE} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import {filter1PItems, filter3PItems} from 'utils/helper';

import {Map} from './Map';
import MapMarkerIcon from './MapMarker';
import {MapWrapperProps} from './types';

declare global {
  interface Window {
    google: any;
  }
}

const render = (status: Status) => {
  if (status === Status.FAILURE) return <Typography>Cannot load Google map</Typography>;
  return <Loading />;
};

export const BangkokCoordinates = {lat: 13.7913253, lng: 100.6149801}; //todo: we should use coordinates of selected zipcode

export const MapWrapper: FC<MapWrapperProps> = ({
  addressString,
  onAddressChange,
  latLng,
  onClick,
  onIdle,
  mapProps,
  address,
  addAddressEvent,
  isToolTipVisible,
  setGeoCodedAddress,
  onZoomChange,
  initialZoomLevel,
  updateLoadingState,
  setHasUserMovedMapManually
}) => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(latLng);
  const [debouncedCenter] = useDebounce(center, 1000);
  const [map, setMap] = useState<google.maps.Map>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const {basket} = useAppSelector((state) => state?.basket);
  const [zoomLevel, setZoomLevel] = useState(initialZoomLevel || config.DEFAULT_ZOOM_LEVEL);
  const [shouldInitializeLocation, setShouldInitializeLocation] = useState(true);
  const initializedLocationRef = useRef(false); //? locate map to homepage subdistrict

  const analytics = useAnalytics();

  useEffect(() => {
    // Check for availability of google.maps.Geocoder
    if (window?.google?.maps?.Geocoder) {
      setGeocoder(new window.google.maps.Geocoder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window?.google?.maps?.Geocoder]);

  //?this is called upon pin move
  const getAddressFromCoordinates = useCallback(
    ({lat, lng}: {lat: number; lng: number}) => {
      if (!geocoder) {
        return;
      }

      updateLoadingState?.(true);

      geocoder.geocode({location: {lat, lng}}, (results, status) => {
        updateLoadingState?.(false);
        if (status === 'OK' && results && results[0]) {
          analytics.trackEvent(ANALYTICS.ADDRESS_DECODED, {
            type: 'geocoding',
            googleResponse: JSON.stringify(results[0]),
            storeId: basket?.storeCode || '',
            channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
            segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
            basketSize: basket?.basketItems?.length || 0,
            onePCount: filter1PItems(basket?.basketItems || []).length || 0,
            threePCount: filter3PItems(basket?.basketItems || []).length || 0
          });
          setGeoCodedAddress?.(results[0]);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geocoder, setGeoCodedAddress]
  );

  const isMapViewGeocodingEnabled = config.IS_MAPVIEW_GEOCODING_ENABLED;

  // handling the initial location
  useEffect(() => {
    if (!geocoder) {
      return;
    }
    if (!shouldInitializeLocation) {
      return;
    }
    if (!debouncedCenter) {
      return;
    }
    setShouldInitializeLocation(false);
    getAddressFromCoordinates(debouncedCenter);
  }, [debouncedCenter, geocoder, getAddressFromCoordinates, shouldInitializeLocation]);

  // handling after pin map drag
  useEffect(() => {
    if (!debouncedCenter || !isMapViewGeocodingEnabled || !geocoder || !initializedLocationRef.current) return;
    getAddressFromCoordinates(debouncedCenter);
  }, [debouncedCenter, getAddressFromCoordinates, isMapViewGeocodingEnabled, geocoder]);

  const geocode = async (request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult | undefined> => {
    if (typeof window != 'undefined' && geocoder) {
      try {
        updateLoadingState?.(true);
        const response = await geocoder.geocode(request);
        const {results} = response;
        const reqUrl = `https://maps.googleapis.com/maps/api/js/GeocodeService.Search?${addressString}`;
        addAddressEvent &&
          addAddressEvent(ANALYTICS.ADDRESS_DECODED, {type: 'geocoding', googleResponse: response, googleUrl: reqUrl});

        setGeoCodedAddress?.(results[0]);
        return results[0];
      } catch (e: any) {
        console.error(`Geocode was not successful for the following reason: ${e.message}`);
      } finally {
        updateLoadingState?.(false);
      }
    }
  };

  useEffect(() => {
    if (center?.lat !== debouncedCenter?.lat && isMapViewGeocodingEnabled && initializedLocationRef.current) {
      updateLoadingState?.(true);
    }
  }, [center?.lat, debouncedCenter?.lat, updateLoadingState, isMapViewGeocodingEnabled]);

  // Handle location gps button press
  const locateUser = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const {latitude, longitude} = position.coords;
          setCenter({lat: latitude, lng: longitude});

          // Pan map to new location
          if (map) {
            map.panTo({lat: latitude, lng: longitude});
          }
        },
        (error) => {
          console.error('Error fetching location:', error);
          setMapLatLngWithAddressString();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setMapLatLngWithAddressString();
      alert('Geolocation is not supported by this browser.');
    }
  };

  const setMapLatLngWithAddressString = async () => {
    const data = await geocode({address: addressString});
    if (data) {
      const lat = data.geometry.location.lat();
      const lng = data.geometry.location.lng();
      setCenter({lat, lng});
      const _latLng = new google.maps.LatLng(lat, lng);
      onAddressChange && onAddressChange(_latLng);
      addAddressEvent && addAddressEvent(ANALYTICS.ADDRESS_LOCATION_PROMPT, {coordinates: {lat, lng}, address});
    }
  };

  useEffect(() => {
    const init = async () => {
      if (latLng && typeof window != 'undefined' && window.google?.maps?.LatLng) {
        setCenter(latLng);
        // if (typeof window != 'undefined' && window.google)
        const _latLng = new window.google.maps.LatLng(latLng.lat, latLng.lng);
        onClick && onClick(_latLng);
        addAddressEvent && addAddressEvent(ANALYTICS.ADDRESS_LOCATION_PROMPT, {coordinates: latLng, address});
      } else if (addressString && geocoder) {
        locateUser();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google, geocoder]);

  const handleMapDrag = async (map: google.maps.Map) => {
    if (!map) {
      return;
    }
    initializedLocationRef.current = true;
    const latLng = map.getCenter();
    if (latLng) {
      onClick && onClick(latLng);
      setHasUserMovedMapManually && setHasUserMovedMapManually(true);
    }
  };

  const _onDragEnd = async (map: google.maps.Map) => {
    if (!map) {
      return;
    }
    const latLng = map.getCenter();

    if (latLng) {
      setCenter({lat: latLng.lat(), lng: latLng.lng()});
    }
  };

  if (!center) {
    return <Loading />;
  }

  return (
    <Wrapper apiKey={config.GOOGLE_MAP_API_KEY} render={render} libraries={['places', 'geometry']}>
      <Map
        center={center}
        gestureHandling="greedy"
        onIdle={onIdle ?? undefined}
        onDragEnd={_onDragEnd}
        onZoomChanged={(map) => {
          if (map) {
            const newZoomLevel = map.getZoom() || config.DEFAULT_ZOOM_LEVEL;
            setZoomLevel(newZoomLevel);
            onZoomChange && onZoomChange(newZoomLevel);

            _onDragEnd(map);
          }
        }}
        zoom={zoomLevel}
        onCenterChanged={handleMapDrag}
        style={{flexGrow: '1', height: '97%'}}
        disableDefaultUI={true}
        map={map}
        setMap={setMap}
        {...mapProps}
      >
        <MapMarkerIcon tooltipVisible={isToolTipVisible} />
        {/* Location Button */}
        <IconButton
          onClick={locateUser}
          sx={{
            position: 'absolute',
            bottom: isMapViewGeocodingEnabled ? 180 : 100,
            left: 30,
            backgroundColor: 'white',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          <MyLocationIcon />
        </IconButton>
      </Map>
    </Wrapper>
  );
};
